import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useParams, useNavigate } from "react-router-dom";
import AthleteProfileCard from "../component/AthleteProfileCard";
import { AthleteTabMenu } from "../athlete_dashboard/AthleteTabMenu";
import API_URL from "../../api/API";
import "chart.js/auto";

const ArrowD = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M13 1L7 7L1 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AdminAthleteRunrocketResult = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [athlete, setAthlete] = useState(null);
  const [performanceData, setPerformanceData] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  const trainingFocuses = [
    "Technical",
    "Speed - Strength",
    "Power",
    "Strength - Speed",
  ];

  // ✅ Fetch athlete basic info
  useEffect(() => {
    const fetchAthlete = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL.GET_ATHLETE_BY_ID}/${athlete_id}`, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });
        const data = await res.json();
        if (res.ok) setAthlete(data);
        else setError(data.error || "Failed to load athlete info");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAthlete();
  }, [athlete_id, staff.token]);

  // ✅ Fetch performance data (same logic as athlete version)
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        setLoading(true);
        const data = {};
        for (const focus of trainingFocuses) {
          const res = await fetch(
            `${API_URL.GET_ADMIN_ATHLETE_RUNROCKET_PERFORMANCE}${athlete_id}/${focus}`,
            { headers: { Authorization: `Bearer ${staff.token}` } },
          );
          if (!res.ok) throw new Error(`Failed to fetch ${focus}`);
          const result = await res.json();
          data[focus] = result.data; // backend returns { "Week 1": [...] }
        }
        setPerformanceData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (athlete_id && staff.token) fetchPerformance();
  }, [athlete_id, staff.token]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const prepareChartData = (focus) => {
    if (!performanceData[focus]) return { labels: [], datasets: [] };

    const labels = [];
    const values = [];

    Object.keys(performanceData[focus]).forEach((weekName) => {
      const weekMatch = weekName.match(/Week\s*(\d+)/i);
      const weekNumber = weekMatch ? weekMatch[1] : "";

      performanceData[focus][weekName].forEach((dayObj) => {
        if (Array.isArray(dayObj.speed_data) && dayObj.speed_data.length > 0) {
          const maxSpeed = Math.max(...dayObj.speed_data.map(Number));
          const shortDay = dayObj.day ? dayObj.day.slice(0, 3) : "";
          labels.push(`W${weekNumber} - ${shortDay}`);
          values.push(maxSpeed);
        }
      });
    });

    return {
      labels,
      datasets: [
        {
          label: `${focus} Max Speed`,
          data: values,
          borderColor: "#c19e42",
          backgroundColor: "#fff",
          tension: 0.25,
          pointRadius: 4,
          pointHoverRadius: 6,
          fontFamily: "Inter",
          fontWeight: 500,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    // plugins: { legend: { display: true } },
    // scales: {
    //   x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
    //   y: { beginAtZero: true, title: { display: true, text: "Speed" } },
    // },
  };

  return (
    <div className="md:flex md:h-screen bg-bgBase">
      <StaffSideMenu />

      <div className="md:flex-1 bg-bgBase overflow-y-auto max-md:pt-4 pb-5">

        <div className="shadow md:sticky md:top-0 z-30">
          {/* Athlete header */}
          <div className="-mb-4">
            {athlete && <AthleteProfileCard athlete={athlete} />}
          </div>

          {/* Tabs */}
          <AthleteTabMenu active="runrocket-result" />
        </div>

        {/* Accordion charts */}
        <div className="pt-10 px-5 md:px-8 xl:px-16">
          <div className="w-full max-w-[1336px] p-5 md:p-6 bg-white rounded-14">
            <div className="mb-6">
              <h2 className="text-dark font-inter font-medium text-xl leading-snug">
                RunRocket Performance Analysis
              </h2>
              <p className="text-slate100 font-inter font-normal text-sm mt-2">
                Comprehensive performance metrics across four key training
                domains
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {trainingFocuses.map((focus, index) => (
                <div
                  key={index}
                  className={`bg-white border border-tblBorder rounded-14 p-4 xl:p-6`}
                >
                  <p className="text-dark font-inter font-medium text-base mb-4">
                    {focus}
                  </p>

                  <div className="pb-6">
                    {loading ? (
                      <p className="text-gray-500">Loading...</p>
                    ) : error ? (
                      <p className="text-red-500">{error}</p>
                    ) : (
                      <Line
                        data={prepareChartData(focus)}
                        options={chartOptions}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAthleteRunrocketResult;
